import { useCallback, useEffect, useState } from 'react';

/**
 * One-time gate backed by localStorage.
 *
 * `useOncePerUser(storageKey)` opens once — on first mount when `storageKey`
 * has never been marked seen — and exposes `markSeen()` to persist the flag and
 * close. Pass a per-user key (e.g. `whats-new:<feature>:<userId>`) so each user
 * sees the announcement once. A `null` key (e.g. before the user resolves) is
 * inert. localStorage failures (private mode / disabled) are swallowed so the
 * app never breaks — the announcement simply doesn't show.
 */
export function useOncePerUser(storageKey: string | null) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!storageKey) return;
    try {
      if (!window.localStorage.getItem(storageKey)) setOpen(true);
    } catch {
      // localStorage unavailable — skip silently.
    }
  }, [storageKey]);

  const markSeen = useCallback(() => {
    if (storageKey) {
      try {
        window.localStorage.setItem(storageKey, '1');
      } catch {
        // ignore write failures
      }
    }
    setOpen(false);
  }, [storageKey]);

  return { open, markSeen };
}

export default useOncePerUser;
